/**
 * EdgeInteractPageRenderer
 * Renders EdgeInteract Explorer page components inside Flow Studio.
 * Wraps pages with MemoryRouter (for react-router hooks).
 *
 * All imports are resolved via Vite aliases in apps/studio/vite.config.ts —
 * no npm install of edge-interact packages is required.
 */

/* eslint-disable @typescript-eslint/ban-ts-comment */

import React from 'react';
import { MemoryRouter, Routes, Route } from 'react-router-dom';

// @ts-ignore — edge-interact explorer CSS (conv-layout, page-container, button, form-*, etc.)
import '@edge-interact/app-styles';

// @ts-ignore — resolved via vite alias → edge-interact/apps/explorer/src/pages
import ConversationsPage from '@edge-interact/app-pages/ConversationsPage';
// @ts-ignore — resolved via vite alias → edge-interact/apps/explorer/src/pages
import FormsPage from '@edge-interact/app-pages/FormsPage';
// @ts-ignore — resolved via vite alias → edge-interact/apps/explorer/src/pages
import SettingsPage from '@edge-interact/app-pages/SettingsPage';
// @ts-ignore — resolved via vite alias → edge-interact/apps/explorer/src/pages
import ExplorerLogsPage from '@edge-interact/app-pages/ExplorerLogsPage';
// @ts-ignore — resolved via vite alias → edge-interact/apps/explorer/src/pages
import { ActivityStreamPage } from '@edge-interact/app-pages/ActivityStreamPage';

import { type ModuleItem } from '../../modules/moduleRegistry';
import { HilTestPage } from '../HIL/HilTestPage.js';

// EdgeInteract green theme CSS variables
const GREEN_THEME_VARS: React.CSSProperties = {
  // Backgrounds
  '--bg-primary':   '#020617',
  '--bg-secondary': '#0f172a',
  '--bg-tertiary':  '#1e293b',
  '--bg-overlay':   'rgba(0, 0, 0, 0.7)',
  // Text
  '--text-primary':   '#f8fafc',
  '--text-secondary': '#94a3b8',
  '--text-muted':     '#64748b',
  '--text-inverse':   '#020617',
  // Borders
  '--border':       'rgba(58, 140, 69, 0.2)',
  '--border-light': 'rgba(58, 140, 69, 0.12)',
  // Accent / primary
  '--obs-primary': '#3A8C45',
  '--primary':     '#3A8C45',
  // Canonical color tokens
  '--color-bg':           '#020617',
  '--color-bg-secondary': '#0f172a',
  '--color-bg-tertiary':  '#1e293b',
  '--color-text-primary':   '#f8fafc',
  '--color-text-secondary': '#94a3b8',
  '--color-border':       'rgba(58, 140, 69, 0.2)',
  '--color-primary':      '#3A8C45',
  // Inputs
  '--input-bg':     'rgba(0, 0, 0, 0.3)',
  '--input-border': 'rgba(58, 140, 69, 0.2)',
  '--input-color':  '#f8fafc',
  '--control-bg':        '#0f172a',
  '--control-bg-nested': '#1e293b',
  '--control-bg-deep':   '#1e293b',
} as React.CSSProperties;

interface EdgeInteractPageRendererProps {
  item: ModuleItem;
}

export const EdgeInteractPageRenderer: React.FC<EdgeInteractPageRendererProps> = ({ item }) => {
  return (
    <div
      className="dark"
      data-theme="dark"
      style={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        background: '#020617',
        color: '#f8fafc',
        ...GREEN_THEME_VARS,
      }}
    >
      <MemoryRouter initialEntries={[item.route]}>
        <Routes>
          <Route path="/conversations"   element={<ConversationsPage />} />
          <Route path="/forms"           element={<FormsPage />} />
          <Route path="/activity-stream" element={<ActivityStreamPage />} />
          <Route path="/explorer-logs"   element={<ExplorerLogsPage />} />
          <Route path="/settings"        element={<SettingsPage />} />
          <Route path="/hil-test"        element={<HilTestPage />} />
        </Routes>
      </MemoryRouter>
    </div>
  );
};
